import {
  ColoringContextEnum,
  ColoringCtxVariable,
  SVGLineStyleCSSClasses,
  SVGLineStyleEnum,
  SVGTextOpacityVariable,
  SwitchingColoringModeEnum,
  VoltageLevelEnum,
} from '~/composables/coloring/constants'
import { EquipmentTypeEnum, SwitchingCategoryTypeEnum } from '@gridcon-energy/schaltungstool-backend'
import { OfflineStatusEnum } from '~/lib/switchings'
import { EquipmentNetworkStateEnum } from '~/lib/equipments'
import type { EquipmentStatusEnum } from '~/lib/equipments'

/**
 * Text Opacity CSS variable
 *
 * @example
 * --svg-text-opacity-category-activated
 */
export type CSSVariableTextOpacityCategory<C extends SwitchingCategoryTypeEnum> =
  `${typeof SVGTextOpacityVariable}-${SwitchingColoringModeEnum.CATEGORY}-${C}`
export function CSSVariableTextOpacityCategory<C extends SwitchingCategoryTypeEnum>(
  switchingCategory: C,
): CSSVariableTextOpacityCategory<C> {
  return `${SVGTextOpacityVariable}-${SwitchingColoringModeEnum.CATEGORY}-${switchingCategory}`
}

/**
 * Text Opacity Project CSS variable
 *
 * @example
 * --svg-text-opacity-project-123
 */
export type CSSVariableTextOpacityProject<P extends 'default' | number> =
  `${typeof SVGTextOpacityVariable}-${SwitchingColoringModeEnum.PROJECT}-${P}`
export function CSSVariableTextOpacityProject<P extends 'default' | number>(
  projectId: P,
): CSSVariableTextOpacityProject<P> {
  return `${SVGTextOpacityVariable}-${SwitchingColoringModeEnum.PROJECT}-${projectId}`
}

/**
 * Text Opacity Voltage CSS variable
 *
 * @example
 * --svg-text-opacity-voltage-380
 */
export type CSSVariableTextOpacityVoltage<V extends VoltageLevelEnum> =
  `${typeof SVGTextOpacityVariable}-${SwitchingColoringModeEnum.VOLTAGE}-${V}`
export function CSSVariableTextOpacityVoltage<V extends VoltageLevelEnum>(
  voltage: V,
): CSSVariableTextOpacityVoltage<V> {
  return `${SVGTextOpacityVariable}-${SwitchingColoringModeEnum.VOLTAGE}-${voltage}`
}

/**
 * Text Opacity Equipment Type CSS variable
 *
 * @example
 * --svg-text-opacity-equipment_type-transformator
 */
export type CSSVariableTextOpacityEquipmentType<P extends EquipmentTypeEnum> =
  `${typeof SVGTextOpacityVariable}-${SwitchingColoringModeEnum.EQUIPMENT_TYPE}-${P}`
export function CSSVariableTextOpacityEquipmentType<P extends EquipmentTypeEnum>(
  equipmentType: P,
): CSSVariableTextOpacityEquipmentType<P> {
  return `${SVGTextOpacityVariable}-${SwitchingColoringModeEnum.EQUIPMENT_TYPE}-${equipmentType}`
}

/**
 * Text Opacity Network State CSS variable
 *
 * @example
 * --svg-text-opacity-network-state-online
 */
export type CSSVariableTextOpacityNetworkState<T extends EquipmentNetworkStateEnum> =
  `${typeof SVGTextOpacityVariable}-network-state-${T}`
export function CSSVariableTextOpacityNetworkState<T extends EquipmentNetworkStateEnum>(
  state: T,
): CSSVariableTextOpacityNetworkState<T> {
  return `${SVGTextOpacityVariable}-network-state-${state}`
}

/**
 * Text Opacity Equipment Status CSS variable
 *
 * @example
 * --svg-text-opacity-equipment-status-existing
 */
export type CSSVariableTextOpacityEquipmentStatus<T extends EquipmentStatusEnum> =
  `${typeof SVGTextOpacityVariable}-equipment-status-${T}`
export function CSSVariableTextOpacityEquipmentStatus<T extends EquipmentStatusEnum>(
  status: T,
): CSSVariableTextOpacityEquipmentStatus<T> {
  return `${SVGTextOpacityVariable}-equipment-status-${status}`
}

/**
 * Text Opacity Offline Range CSS variable
 *
 * @example
 * --svg-text-opacity-offline-range-pendingActivation
 */
export type CSSVariableTextOpacityOfflineRange<O extends OfflineStatusEnum> =
  `${typeof SVGTextOpacityVariable}-offline-range-${O}`
export function CSSVariableTextOpacityOfflineRange<O extends OfflineStatusEnum>(
  type: O,
): CSSVariableTextOpacityOfflineRange<O> {
  return `${SVGTextOpacityVariable}-offline-range-${type}`
}

/**
 * @example
 * {
 *   --color-gantt: var(--gantt-category-activated)
 *   --color-svg: var(--svg-project-123)
 *   --color-svg: var(--svg-offline-range-pendingActivation)
 * }
 */
export function CSSStyleColoring<T extends ColoringContextEnum>(
  ctx: T,
  modeCssVar: `--${T}-${string}`,
): Record<string, string> {
  return { [ColoringCtxVariable[ctx]]: `var(${modeCssVar})` }
}

/**
 * Helper to set text opacity CSS variable for SVG
 * @example
 * {
 *   --text-opacity-svg: var(--svg-text-opacity-category-activated)
 * }
 */
export function CSSStyleTextOpacity(modeCssVar: `${typeof SVGTextOpacityVariable}-${string}`): Record<string, string> {
  return { [SVGTextOpacityVariable]: `var(${modeCssVar})` }
}

/**
 * Category CSS variable
 *
 * @example
 * --gantt-category-activated
 */
export type CSSVariableCategoryColoring<
  T extends ColoringContextEnum,
  C extends SwitchingCategoryTypeEnum,
> = `--${T}-${SwitchingColoringModeEnum.CATEGORY}-${C}`
export function CSSVariableCategory<T extends ColoringContextEnum, C extends SwitchingCategoryTypeEnum>(
  ctx: T,
  switchingCategory: C,
): CSSVariableCategoryColoring<T, C> {
  return `--${ctx}-${SwitchingColoringModeEnum.CATEGORY}-${switchingCategory}`
}

/**
 * Project CSS variable
 *
 * @example
 * --gantt-project-123
 */
export type CSSVariableProjectColoring<
  T extends ColoringContextEnum,
  P extends 'default' | number,
> = `--${T}-${SwitchingColoringModeEnum.PROJECT}-${P}`
export function CSSVariableProject<T extends ColoringContextEnum, P extends 'default' | number>(
  ctx: T,
  projectId: P,
): CSSVariableProjectColoring<T, P> {
  return `--${ctx}-${SwitchingColoringModeEnum.PROJECT}-${projectId}`
}

/**
 * Voltage CSS variable
 *
 * @example
 * --gantt-voltage-380
 */
export type CSSVariableVoltageColoring<
  T extends ColoringContextEnum,
  V extends VoltageLevelEnum,
> = `--${T}-${SwitchingColoringModeEnum.VOLTAGE}-${V}`
export function CSSVariableVoltage<T extends ColoringContextEnum, V extends VoltageLevelEnum>(
  ctx: T,
  voltage: V,
): CSSVariableVoltageColoring<T, V> {
  return `--${ctx}-${SwitchingColoringModeEnum.VOLTAGE}-${voltage}`
}

/**
 * Offline Range CSS variable
 *
 * @example
 * --gantt-offline-range-pendingActivation
 */
export type CSSVariableOfflineRange<
  T extends ColoringContextEnum,
  O extends OfflineStatusEnum,
> = `--${T}-offline-range-${O}`
export function CSSVariableOfflineRange<T extends ColoringContextEnum, O extends OfflineStatusEnum>(
  ctx: T,
  type: O,
): CSSVariableOfflineRange<T, O> {
  return `--${ctx}-offline-range-${type}`
}

export type CSSVariableNetworkState<T extends EquipmentNetworkStateEnum> = `--svg-network-state-${T}`
export function CSSVariableNetworkState<T extends EquipmentNetworkStateEnum>(state: T): CSSVariableNetworkState<T> {
  return `--svg-network-state-${state}`
}

export type CSSVariableEquipmentStatus<T extends EquipmentStatusEnum> = `--svg-equipment-status-${T}`
export function CSSVariableEquipmentStatus<T extends EquipmentStatusEnum>(status: T): CSSVariableEquipmentStatus<T> {
  return `--svg-equipment-status-${status}`
}

/**
 * Equipment Type CSS variable
 *
 * @example
 * --gantt-equipment_type-transformator
 */
export type CSSVariableEquipmentTypeColoring<
  T extends ColoringContextEnum,
  P extends EquipmentTypeEnum,
> = `--${T}-${SwitchingColoringModeEnum.EQUIPMENT_TYPE}-${P}`
export function CSSVariableEquipmentType<T extends ColoringContextEnum, P extends EquipmentTypeEnum>(
  ctx: T,
  equipmentType: P,
): CSSVariableEquipmentTypeColoring<T, P> {
  return `--${ctx}-${SwitchingColoringModeEnum.EQUIPMENT_TYPE}-${equipmentType}`
}

export function classSVG(lineStyle: SVGLineStyleEnum): string[] {
  return [SVGLineStyleCSSClasses[lineStyle]]
}
